import type { QueryClient } from '@tanstack/react-query';
import type {
  PrintConfig,
  CreateOrderResponse,
  CreateOrderRequest,
  Customer,
  PaymentType,
  ReceiptAddress,
  ReceiptData,
} from '@matix/types';
import { getTenantTerminology, roundMoney } from '@matix/utils';
import type { CartLine, CartTable } from '../../store/cart';
import { lineDiscountAmount, lineNetTotal } from '../../store/cart';
import { useReceiptStore } from '../../store/receipt';
import {
  buildReceiptData,
  buildSalesmanSummarySection,
  receiptModifierName,
  usesBilingualReceipt,
  type PrintSection,
} from '@matix/receipt';
import { useTenantConfig } from '../../store/tenantConfig';
import { PRINT_CONFIG_QUERY_KEY } from './usePrintConfig';
import { currentPrintLanguage } from './printLanguage';

/**
 * Build the receipt's table line ("Sun bed: 5" / "Table: 7") using the
 * tenant's terminology. The cashier JWT carries `tenant_db` rather than a
 * slug; we derive the slug by stripping the `GL_WEB_` prefix so the same
 * `getTenantTerminology` dictionary the rest of the app uses can resolve.
 *
 * Returns null when no table is attached — the receipt template hides the
 * row in that case (walk-in / takeaway flow).
 */
function formatReceiptTableLine(
  table: CartTable | null | undefined,
  tenantDb: string | null | undefined,
): string | null {
  if (!table) return null;
  const slug = tenantDb && tenantDb.toLowerCase().startsWith('gl_web_')
    ? tenantDb.slice('gl_web_'.length)
    : tenantDb ?? null;
  const term = getTenantTerminology(slug).table.en;
  return `${term}: ${table.tableNumber}`;
}

/**
 * The address this sale goes on the paper with.
 *
 * The one the cashier PICKED in the delivery modal wins — that is the address
 * the driver was sent to, and a customer who keeps three addresses would
 * otherwise be handed a slip for the wrong one. With no delivery leg we fall
 * back to their most recent stored address, which is the same row the delivery
 * modal itself pre-selects, so the two never disagree about "the" address.
 *
 * Null when the customer has no structured address at all; a CREDIT customer
 * has none by construction (`Inv_CashCustomerDetail` holds cash-customer rows
 * only) and prints the free-text `address` line instead.
 */
function receiptAddressOf(
  customer: Customer,
  request: CreateOrderRequest,
): ReceiptAddress | null {
  const addresses = customer.addresses ?? [];
  if (addresses.length === 0) return null;

  const pickedId = request.delivery?.address_id ?? null;
  const chosen =
    (pickedId != null ? addresses.find((a) => a.id === pickedId) : undefined) ?? addresses[0];
  if (!chosen) return null;

  return {
    city: chosen.governorate_name,
    cityAr: chosen.governorate_name_ar,
    district: chosen.area_name,
    districtAr: chosen.area_name_ar,
    block: chosen.block,
    street: chosen.street,
    avenue: chosen.avenue,
    house: chosen.house,
    floor: chosen.floor,
    flat: chosen.flat,
    note: chosen.address_note,
  };
}

/**
 * Glue between the live PaymentModal state and the receipt printer.
 *
 * Call this from the `usePlaceOrder` `onSuccess` callback, immediately after
 * any cart-clear / modal-close work. It:
 *
 *   1. Reads the cached `PrintConfig` from React Query (warmed at login by
 *      `usePrintConfig` mounted in App.tsx — no extra round-trip on Charge).
 *   2. Maps the legacy field names that the pos-api response uses
 *      (`entry_no`, `totals.net_value`, `totals.discount_value`, `totals.tax_value`)
 *      onto the receipt template's expected shape (`code`, `total`, `discount`,
 *      `tax`).
 *   3. Looks up each payment split's localized label and `is_cash` flag from
 *      the payment-types list the modal already has loaded.
 *   4. Pushes the resulting ReceiptData into the receipt store, which
 *      auto-prints and shows the "Receipt printed" toast.
 *
 * Best-effort: if the print config has not loaded yet (network blip during
 * boot, 5xx during the initial fetch), this returns silently — the charge
 * already succeeded, so we never let print failures hang the cashier flow.
 *
 * Errors during `print()` itself are swallowed inside the receipt store
 * (the `printing` flag flips back to false on rejection) so the cashier can
 * always retry via the toast's "Print again" button.
 */
export interface PrintAfterChargeArgs {
  queryClient: QueryClient;

  /** Whatever POST /api/v1/orders returned. */
  response: CreateOrderResponse;

  /** The CreateOrderRequest you passed to `placeOrder.mutate(req)`. */
  request: CreateOrderRequest;

  /** Cart lines as they were at the moment of Charge. */
  cartLines: CartLine[];

  /** Attached customer (or null for walk-in cash). */
  customer: Customer | null;

  /**
   * Attached BranchTables row from the cart (dine-in). When present, the
   * receipt template prints a "Sun bed: 5" / "Table: 7" row right under
   * the cashier name. Null for walk-in receipts.
   */
  table: CartTable | null;

  /**
   * Tenant DB name (e.g. `GL_WEB_villashams_test`) — used to resolve the
   * tenant's table terminology. Strip the `GL_WEB_` prefix to get the slug
   * the `@matix/utils` terminology dictionary expects.
   */
  tenantDb: string | null;

  /**
   * Cashier display info. Use whichever name you want printed; the receipt
   * Both scripts of the person's real name (Security_Users.Name / .Name_AR);
   * the receipt picks by the slip's language. Build it with
   * `cashierIdentity()`, which also carries the login-name fallback for a
   * token minted before those claims existed.
   */
  cashier: { id: number; name: string | null; nameAr?: string | null };

  /**
   * The full payment-type list the modal already has loaded
   * (`paymentAPI.listTypes()`). Used to look up each split's localized label
   * and is_cash flag from its `type_code`.
   */
  paymentTypes: PaymentType[];

  /**
   * Print this instead of the server's `entry_no`.
   *
   * Only an offline sale passes it. The invoice number is allocated inside the
   * write transaction, so a sale taken with no network does not have one yet —
   * it prints a provisional `OFF-…` code that cannot be mistaken for a real
   * invoice number, and the real one appears on the sync screen once it posts.
   */
  displayCode?: string | null;
}

/**
 * Returns what it printed, so an offline sale can keep the receipt in its
 * queue entry and re-print it later without rebuilding it. Null when the print
 * config was not loaded and nothing was printed.
 */
export function printAfterCharge(args: PrintAfterChargeArgs): ReceiptData | null {
  const printConfig = args.queryClient.getQueryData<PrintConfig>(PRINT_CONFIG_QUERY_KEY);
  if (!printConfig) {
    // Print config not loaded yet — the charge already succeeded, so we do
    // not surface this to the cashier. The "Print again" button will be
    // unavailable until next login warms the cache.
    return null;
  }

  const paymentTypeByCode = new Map<number, PaymentType>(
    args.paymentTypes.map((pt) => [pt.code, pt])
  );

  // Round the printed line totals at the tenant currency's precision — the same
  // rule the cart and the server use. Rounding at a hardcoded 2 while the
  // template formats at the currency's precision printed "0.140 KWD" for a
  // 1 × 0.136 line, and the lines stopped adding up to the printed total.
  const rm = (n: number): number => roundMoney(n, printConfig.currency?.precision);

  const hasReturns = args.cartLines.some((l) => l.isReturn);

  // The language the slip will print in — resolved here rather than in the
  // renderer because option names are flattened to one string per option
  // before they get there. Same source the receipt store stamps onto
  // `ReceiptData.language`, so the two can never disagree.
  const lang = currentPrintLanguage();
  // GL_WEB_ablaatiaat's slip carries both scripts everywhere — see
  // `bilingualReceipt.ts`. Options are flattened here, so the flag has to be
  // read here too.
  const bilingual = usesBilingualReceipt(printConfig);

  const salesmanById = new Map(
    (args.queryClient.getQueryData<Array<{ id: number; name: string; name_ar: string | null }>>(['salesmen']) ?? [])
      .map((s) => [s.id, s] as const),
  );

  const promoShares = args.response.promotion_line_discounts ?? [];
  let saleIndex = 0;

  const data = buildReceiptData({
    printConfig,
    orderResponse: {
      order_id: args.response.order_id,
      // Server returns `entry_no` (per-subsidiary cashier-facing receipt
      // number); we map it onto the template's `code` field so the receipt
      // prints "Receipt #N" using the cashier-facing number, not the
      // internal DB id.
      code: args.displayCode ?? args.response.entry_no,
      entry_date: args.response.entry_date,
      // The current /orders response does not return change_due. The
      // buildReceiptData fallback (cashTendered - total, clamped) covers it.
      //
      // How many times this document has now been issued — 1 for a new sale,
      // higher when the cashier just saved an EDIT. Carried from the response
      // rather than assumed, so the copy handed over at the counter shows the
      // same mark as any later reprint of it. An older API omits it and the
      // slip prints as it always has.
      ...(args.response.print_count != null
        ? { print_count: args.response.print_count }
        : {}),
      // An EDIT's response names who saved it and when, so the copy printed
      // now carries the same "edited by / on" as any later reprint.
      ...(args.response.last_edit ? { last_edit: args.response.last_edit } : {}),
      //
      // Automatic promotions, named. The money is already inside the discount
      // total; these let the receipt say WHAT the customer was given rather
      // than showing a deduction nobody at the counter can account for.
      ...(args.response.promotions && args.response.promotions.length > 0
        ? { promotions: args.response.promotions }
        : {}),
    },
    items: args.cartLines.map((line) => {
      const sign = line.isReturn ? -1 : 1;
      // The line's share of an item-level promotion, which the server booked on
      // the line (response order = the sale lines as sent). Printed as part of
      // the line's discount, exactly as the receipt stores it.
      const promo = line.isReturn ? 0 : (promoShares[saleIndex++] ?? 0);
      // The line's salesman (both slots), by name, in each script — from the
      // ['salesmen'] query the cart panel keeps warm. A cold cache prints no
      // name rather than an id nobody at the counter can read.
      const salesmen = [line.salesman1Id, line.salesman2Id]
        .filter((id): id is number => id != null)
        .map((id) => salesmanById.get(id))
        .filter((s): s is NonNullable<typeof s> => s != null);
      const salesman = salesmen.map((s) => s.name).filter(Boolean).join(' / ') || null;
      const salesmanAr = salesmen.map((s) => s.name_ar || s.name).filter(Boolean).join(' / ') || null;
      return {
        salesman,
        salesmanAr,
        // CatalogItem has nullable name fields; fall back to the item code so
        // the receipt never prints an empty line.
        name: line.product.name ?? '',
        nameAr: line.product.nameAr,
        // Item code / barcode carried from the catalog — printed on the
        // receipt only when the tenant's print options enable them.
        code: line.product.code ?? null,
        barcode: line.product.barcode ?? null,
        // The unit, named only on a multi-unit item (the catalog sets these
        // names only then), so a single-unit item prints as it always did.
        ...(line.product.unitName || line.product.unitNameAr
          ? { unit: line.product.unitName ?? null, unitAr: line.product.unitNameAr ?? null }
          : {}),
        // Per-line note and selected modifiers — printed when IsPrintCustomOptions.
        note: line.note ?? null,
        // ONE language per option — the slip's own. Everything else on a line
        // carries both scripts through to `buildReceiptHtml`, which picks
        // there; options cannot, because `ReceiptData.mods` is a flat
        // `string[]` by the time the renderer sees it. Taking `m.name` blindly
        // is what handed an Arabic-speaking customer a receipt whose item names
        // were Arabic and whose options were English — and a reprint of that
        // same receipt then printed them in Arabic, because
        // `buildReprintData` has always resolved this properly.
        mods: line.mods
          .map((m) => receiptModifierName(m, { arabic: lang === 'ar', bilingual }))
          .filter((n) => n.length > 0),
        qty: line.qty,
        // The unit price BEFORE the line's own discount, which is what the
        // "qty × price" row means — the discount then prints beneath it as its
        // own row and the line total is the two reconciled. Printing an
        // already-discounted unit price instead would hide the discount from
        // the customer while still charging the discounted total.
        price: line.unitPrice,
        discount: promo > 0 ? rm(lineDiscountAmount(line, rm) + promo) : lineDiscountAmount(line, rm),
        total: sign * (promo > 0 ? rm(lineNetTotal(line, rm) - promo) : lineNetTotal(line, rm)),
        ...(line.isReturn ? { isReturn: true } : {}),
      };
    }),
    totals: {
      subtotal: args.response.totals.subtotal,
      discount: args.response.totals.discount_value,
      // Its own header column, never inside the subtotal. Without it the paper
      // does not add up on a delivery order — see ReceiptData.totals.
      deliveryFee: args.response.totals.delivery_fee,
      tax: args.response.totals.tax_value,
      total: args.response.totals.net_value,
    },
    // Exchange/return: drives the grouped Refund + Invoice + Net printout.
    ...(hasReturns
      ? {
          exchange: {
            soldTotal: args.request.expected_total ?? args.response.totals.net_value,
            returnedTotal: args.request.expected_return_total ?? 0,
            netDifference:
              args.response.net_settled ??
              rm((args.request.expected_total ?? 0) - (args.request.expected_return_total ?? 0)),
            saleEntryNo: args.response.sale_receipt?.entry_no ?? null,
            returnEntryNo: args.response.return_receipt?.entry_no ?? null,
          },
        }
      : {}),
    // On an exchange, list both the collected splits and the refunded splits
    // (refunds shown as negative) so the receipt reflects the full settlement.
    payments: [
      ...args.request.payments.map((p) => {
        const meta = paymentTypeByCode.get(p.type_code);
        return {
          typeName: meta?.name ?? `Payment ${p.type_code}`,
          typeNameAr: meta?.name_ar ?? null,
          amount: p.amount,
          isCash: meta?.is_cash ?? false,
        };
      }),
      // Points the server booked as a payment split rather than a discount.
      ...((args.response.loyalty?.paid_as_tender ?? 0) > 0
        ? [{
            typeName: paymentTypeByCode.get(-2)?.name || 'Loyalty',
            typeNameAr: paymentTypeByCode.get(-2)?.name_ar || 'نقاط الولاء',
            amount: args.response.loyalty!.paid_as_tender!,
            isCash: false,
          }]
        : []),
      ...(args.request.refund_payments ?? []).map((p) => {
        const meta = paymentTypeByCode.get(p.type_code);
        return {
          typeName: `${meta?.name ?? `Payment ${p.type_code}`} (refund)`,
          typeNameAr: meta?.name_ar ? `${meta.name_ar} (مرتجع)` : null,
          amount: -p.amount,
          isCash: meta?.is_cash ?? false,
        };
      }),
    ],
    cashTendered: args.request.cash_tendered,
    cashier: args.cashier,
    tableLabel: formatReceiptTableLine(args.table, args.tenantDb),
    // The server writes the order's reference number into Inv_Receipt_h.Memo
    // (orders.service), so the first print's Memo box matches every reprint.
    // A delivery order carries its reference inside the delivery block (the
    // delivery form's box), which the server writes to Memo ahead of the
    // top-level box — read it the same way or only the reprint shows it.
    memo: args.request.delivery?.reference_no?.trim() || args.request.reference_no || null,
    // The payment modal's two boxes, so the sheet that comes out of the
    // printer now carries exactly what a reprint of it will.
    referenceNo: args.request.delivery?.reference_no?.trim() || args.request.reference_no || null,
    notes: args.request.note ?? null,
    // The promised delivery time, on a delivery order only. The server's copy
    // wins (it is the tenant wall-clock actually written to the receipt); the
    // request's own value covers an API build that does not echo it back.
    deliveryDate:
      args.request.order_type === 2
        ? args.response.delivery?.delivery_date ?? args.request.delivery?.delivery_date ?? null
        : null,
    // The type the sale was charged as. Only a custom design prints it, and it
    // comes from the request so the slip and every later reprint (which reads
    // Inv_Receipt_h.OrderType, written from this same field) agree.
    orderType: args.request.order_type ?? null,
    // The order's VAT rate, so the A4 tax invoice can print a per-line tax
    // column and label its VAT row instead of deriving the rate from totals.
    taxPercent: args.request.tax_percentage ?? null,
    customer: args.customer
      ? {
        type: args.customer.type,
        // A B2B tax invoice has to carry the buyer's VAT number and address,
        // not just their name — the A4 sheet prints both when they exist.
        taxNumber: args.customer.tax_reg_no,
        address: args.customer.address,
        addressDetail: receiptAddressOf(args.customer, args.request),
        name: args.customer.name,
        // The shared Customer type uses snake_case (`name_ar`); the
        // receipt template uses camelCase (`nameAr`). Keep the boundary
        // here.
        nameAr: args.customer.name_ar,
        phone: args.customer.phone,
      }
      : undefined,
  });

  // Fire-and-forget: pushAndPrint awaits print() internally and resets the
  // printing flag on failure. We do not block the cashier on the print step.
  void useReceiptStore.getState().pushAndPrint(data, salesmanSummarySection(args, printConfig));

  return data;
}

/**
 * `IsPrintSalesManSummary` — a second document listing each salesman's items,
 * printed straight after the receipt exactly as the legacy POS did
 * (support-codes.js:32 → PrePrintSalesmanSummary). It goes out on the SAME
 * print job as the receipt, behind a page break: a second window of its own
 * meant two tabs and two print dialogs per sale, and the second dialog could
 * open in a tab the cashier had already left, freezing the till behind it.
 *
 * Salesman NAMES are not on the cart line (only ids), so they come from the
 * `['salesmen']` query the cart panel already keeps warm. Reading the cache
 * here avoids threading a new argument through all three callers of
 * printAfterCharge. A cold cache means no names, which would make the document
 * meaningless — so we skip it rather than print a sheet of "Unassigned".
 *
 * Entirely best-effort: the sale is already posted, and a failure here must
 * never surface as a failed charge. Null when the tenant does not print it.
 */
function salesmanSummarySection(
  args: PrintAfterChargeArgs,
  cfg: PrintConfig,
): PrintSection | null {
  try {
    if (!useTenantConfig.getState().printSalesmanSummary) return null;

    const salesmen = args.queryClient.getQueryData<Array<{ id: number; name: string; name_ar: string | null }>>(
      ['salesmen'],
    );
    if (!salesmen || salesmen.length === 0) return null;
    const nameById = new Map(salesmen.map((s) => [s.id, s]));

    const arabic = cfg.printOptions?.arabic === true;
    const items = args.cartLines
      // Returned lines are not a salesman's sale, and the legacy sheet only
      // ever listed what went out.
      .filter((line) => !line.isReturn)
      .map((line) => {
        const s = line.salesman1Id != null ? nameById.get(line.salesman1Id) : undefined;
        return {
          name: line.product.name ?? '',
          nameAr: line.product.nameAr,
          barcode: line.product.barcode ?? null,
          qty: line.qty,
          salesmanName: s ? (arabic ? s.name_ar || s.name : s.name) : null,
        };
      });

    return buildSalesmanSummarySection({
      invoiceNo: args.response.entry_no ?? null,
      entryDate: args.response.entry_date,
      customerName: args.customer?.name ?? null,
      items,
      showBarcode: cfg.printOptions?.showItemBarcode === true,
      arabic,
    });
  } catch {
    // Swallowed on purpose — see the note above.
    return null;
  }
}
